/**
 * Birthday Parties - video controls
 *
 * Enhances the header videos. The page is fully functional without this file:
 * the videos carry autoplay/muted/loop/playsinline attributes and the enquiry
 * form is server-rendered.
 *
 * Three things here are not obvious:
 *
 * 1. video.volume is READ-ONLY on iOS - audio level is under hardware control
 *    and reads always return 1. So the volume slider ships hidden and is only
 *    revealed once we have proven the value can actually be changed. The mute
 *    toggle is the primary control because it works everywhere.
 *
 * 2. Unmuting must happen synchronously inside the click handler. WebKit pauses
 *    playback if a video un-mutes without a user gesture, and a value set in a
 *    promise callback or timeout no longer counts as one.
 *
 * 3. Button state is rendered from media events, never from a local boolean.
 *    WebKit pauses autoplaying video that scrolls out of the viewport and
 *    resumes it on re-entry, entirely outside our control - tracking state
 *    ourselves would drift out of sync with what is actually on screen.
 *
 * Auto-initializes on DOMContentLoaded:
 *   [data-bp-background] -> background loop
 *   [data-bp-reel]       -> reel player with controls
 */

(function () {
  'use strict';

  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Attempt playback and record failure rather than retrying.
   * Autoplay is refused in several cases we cannot influence - iOS Low Power
   * Mode blocks it even for muted inline video. Retrying makes it worse.
   */
  function attemptPlay(video, onBlocked) {
    var attempt = video.play();

    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () {
        if (onBlocked) onBlocked();
      });
    }
  }

  /**
   * The volume property is silently ignored on iOS. Probe it, then restore
   * whatever the value was so the check leaves no trace.
   */
  function volumeIsSettable(video) {
    var original = video.volume;
    var settable;

    try {
      video.volume = 0.5;
      settable = Math.abs(video.volume - 0.5) < 0.01;
      video.volume = original;
    } catch (e) {
      settable = false;
    }

    return settable;
  }

  function initBackground(video) {
    if (prefersReducedMotion) {
      video.removeAttribute('autoplay');
      video.pause();
      return;
    }

    attemptPlay(video);
  }

  function initReel(container) {
    var video = container.querySelector('video');
    if (!video) return;

    var playButton = container.querySelector('[data-bp-play]');
    var muteButton = container.querySelector('[data-bp-mute]');
    var volumeInput = container.querySelector('[data-bp-volume]');

    function syncPlayState() {
      if (!playButton) return;
      var playing = !video.paused && !video.ended;
      playButton.setAttribute('data-state', playing ? 'playing' : 'paused');
      playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }

    function syncSoundState() {
      var silent = video.muted || video.volume === 0;

      if (muteButton) {
        muteButton.setAttribute('data-state', silent ? 'muted' : 'unmuted');
        muteButton.setAttribute('aria-label', silent ? 'Unmute video' : 'Mute video');
      }

      if (volumeInput && !volumeInput.hidden) {
        volumeInput.value = video.muted ? 0 : video.volume;
      }
    }

    // Render from what the element is actually doing, not from our own flags.
    video.addEventListener('play', syncPlayState);
    video.addEventListener('pause', syncPlayState);
    video.addEventListener('ended', syncPlayState);
    video.addEventListener('volumechange', syncSoundState);

    if (playButton) {
      playButton.addEventListener('click', function () {
        if (video.paused) {
          container.removeAttribute('data-autoplay-blocked');
          attemptPlay(video);
        } else {
          video.pause();
        }
      });
    }

    if (muteButton) {
      muteButton.addEventListener('click', function () {
        // Synchronous - see note 2 at the top of this file.
        video.muted = !video.muted;

        // Unmuting a video sitting at zero volume would look broken.
        if (!video.muted && video.volume === 0) {
          video.volume = 1;
        }
      });
    }

    if (volumeInput) {
      if (volumeIsSettable(video)) {
        volumeInput.hidden = false;
        volumeInput.value = video.muted ? 0 : video.volume;

        volumeInput.addEventListener('input', function () {
          var level = parseFloat(volumeInput.value);
          video.volume = level;
          // Dragging the slider up is an unmute request.
          video.muted = level === 0;
        });
      }
      // Left hidden on iOS, where the mute toggle is the only usable control.
    }

    if (prefersReducedMotion) {
      video.removeAttribute('autoplay');
      video.pause();
    } else {
      attemptPlay(video, function () {
        container.setAttribute('data-autoplay-blocked', '');
      });
    }

    syncPlayState();
    syncSoundState();
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-bp-background]').forEach(function (video) {
      initBackground(video);
    });

    document.querySelectorAll('[data-bp-reel]').forEach(function (container) {
      initReel(container);
    });
  });
})();
